import { useEffect, useState } from "react";
import {
  ApiError,
  CreditBalance,
  CustomerInvoice,
  CustomerInvoiceDetail,
  api,
} from "../lib/api";
import { useAuth } from "../lib/auth";

function fmtDate(d: string | null): string {
  if (!d) return "-";
  return new Date(`${d}T00:00:00`).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** Quantities are DECIMAL server-side (1.5 video minutes, 300 pages), so show
 *  a fraction only when there actually is one. */
function fmtQty(n: number): string {
  return Number.isInteger(n) ? n.toLocaleString() : n.toFixed(1);
}

function gb(n: number): string {
  return `${n.toLocaleString(undefined, {
    maximumFractionDigits: n < 10 ? 1 : 0,
  })} GB`;
}

/** Invoices for periods that closed before storage billing existed carry zeros
 *  in every storage column. That is honest data, but rendering it as
 *  "0 GB of 0 GB" reads like a broken meter rather than "not billed for". */
function hasStorageTerms(inv: CustomerInvoice): boolean {
  return (
    inv.included_storage_gb > 0 ||
    inv.storage_overage_usd_per_gb > 0 ||
    inv.peak_storage_gb > 0
  );
}

export default function Credits() {
  const role = useAuth((s) => s.user?.role);
  // Statements summarise the whole account, so they are limited to the roles
  // that run it. Everyone else still gets the usage view above them.
  const canSeeInvoices = role === "owner" || role === "admin";

  const [bal, setBal] = useState<CreditBalance | null>(null);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [invoices, setInvoices] = useState<CustomerInvoice[]>([]);
  const [invoiceErr, setInvoiceErr] = useState<string | null>(null);
  const [detail, setDetail] = useState<CustomerInvoiceDetail | null>(null);
  const [detailBusy, setDetailBusy] = useState(false);

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    if (!canSeeInvoices) return;
    void loadInvoices();
  }, [canSeeInvoices]);

  async function load() {
    setBusy(true);
    setErr(null);
    try {
      setBal(await api.get<CreditBalance>("/credits/balance"));
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.message : "Failed to load credit balance");
    } finally {
      setBusy(false);
    }
  }

  /** Kept separate from the balance load so a billing-history failure never
   *  takes down the usage view, which is the reason most people open this page. */
  async function loadInvoices() {
    setInvoiceErr(null);
    try {
      setInvoices(await api.get<CustomerInvoice[]>("/credits/invoices"));
    } catch (e: unknown) {
      setInvoiceErr(
        e instanceof ApiError ? e.message : "Failed to load billing history",
      );
    }
  }

  async function openInvoice(inv: CustomerInvoice) {
    setDetailBusy(true);
    setInvoiceErr(null);
    try {
      setDetail(
        await api.get<CustomerInvoiceDetail>(`/credits/invoices/${inv.id}`),
      );
    } catch (e: unknown) {
      setInvoiceErr(
        e instanceof ApiError ? e.message : "Failed to load invoice",
      );
    } finally {
      setDetailBusy(false);
    }
  }

  if (busy) {
    return (
      <div className="admin">
        <header className="admin__head">
          <div>
            <div className="page-eyebrow">Usage</div>
            <h1>Credits</h1>
          </div>
        </header>
        <p className="muted">Loading...</p>
      </div>
    );
  }

  if (err) {
    return (
      <div className="admin">
        <header className="admin__head">
          <div>
            <div className="page-eyebrow">Usage</div>
            <h1>Credits</h1>
          </div>
        </header>
        <div className="alert alert--error">{err}</div>
      </div>
    );
  }

  if (!bal || !bal.on_plan) {
    return (
      <div className="admin">
        <header className="admin__head">
          <div>
            <div className="page-eyebrow">Usage</div>
            <h1>Credits</h1>
          </div>
        </header>
        <div className="admin-card">
          <p className="admin-card__blurb">
            This organization is not on a credit plan, so nothing is being
            metered. Contact your account manager to choose a plan.
          </p>
        </div>
      </div>
    );
  }

  const over = bal.remaining < 0;
  // Cap the bar at 100% and show overage as a separate figure. A bar that
  // silently overflows its track reads as a rendering bug.
  const barPct = Math.min(100, Math.max(0, bal.pct_used));
  const barTone = over ? "danger" : bal.alert_state === "warning" ? "warn" : "ok";
  const totalActionCredits = bal.breakdown.reduce((s, l) => s + l.credits, 0);

  const storageOver = bal.storage_overage_gb > 0;
  const storageBarPct = Math.min(100, Math.max(0, bal.storage_pct_used));
  const storageTone = storageOver
    ? "danger"
    : bal.storage_alert_state === "warning"
      ? "warn"
      : "ok";

  return (
    <div className="admin admin--page-scroll">
      <header className="admin__head">
        <div>
          <div className="page-eyebrow">Usage</div>
          <h1>Credits</h1>
          <p className="admin__lead">
            Credits are consumed by AI work: asking questions, extracting
            timelines, processing video, and translating documents. Your
            allowance refreshes at the start of each billing period. Storage is
            metered separately, against the files you keep.
          </p>
        </div>
        <div className="admin__head-actions">
          <button
            type="button"
            className="btn"
            onClick={() => {
              void load();
              if (canSeeInvoices) void loadInvoices();
            }}
          >
            Refresh
          </button>
        </div>
      </header>

      {over && (
        <div className="alert alert--error" role="status">
          <strong>
            You are {Math.abs(bal.remaining).toLocaleString()} credits past your
            allowance.
          </strong>{" "}
          Nothing is blocked and no work has stopped. The extra{" "}
          {bal.overage_credits.toLocaleString()} credits are recorded against
          this period and settled on your invoice.
        </div>
      )}
      {!over && bal.alert_state === "warning" && (
        <div className="alert alert--warning" role="status">
          You have used {bal.pct_used.toFixed(0)}% of this period's credits,
          past your {bal.alert_threshold_pct}% notification threshold. Going
          over will not interrupt anything - the excess is simply recorded as
          overage.
        </div>
      )}
      {storageOver && (
        <div className="alert alert--warning" role="status">
          <strong>
            You are storing {gb(bal.storage_overage_gb)} more than your{" "}
            {gb(bal.included_storage_gb)} allowance.
          </strong>{" "}
          Uploads are not blocked. Deleting files frees space immediately, but
          this period is still recorded at its highest reading.
        </div>
      )}

      <div className="credits__hero admin-card">
        <div className="credits__figure">
          <div className={`credits__remaining credits__remaining--${barTone}`}>
            {over
              ? `-${Math.abs(bal.remaining).toLocaleString()}`
              : bal.remaining.toLocaleString()}
          </div>
          <div className="credits__figure-label">
            {over ? "credits over allowance" : "credits remaining"}
          </div>
        </div>

        <div className="credits__meter">
          <div className="credits__meter-head">
            <span>
              {bal.credits_used.toLocaleString()} of{" "}
              {bal.included_credits.toLocaleString()} used
            </span>
            <span className="muted">{bal.pct_used.toFixed(1)}%</span>
          </div>
          <div className="progress credits__bar">
            <div
              className={`progress__bar credits__bar-fill--${barTone}`}
              style={{ width: `${barPct}%` }}
            />
          </div>
          <div className="credits__meta muted">
            <span>
              {bal.tier_name ? `${bal.tier_name} plan` : "Custom plan"}
            </span>
            <span>
              {fmtDate(bal.period_start)} to {fmtDate(bal.period_end)}
            </span>
            <span>
              {bal.days_remaining === 0
                ? "renews today"
                : `${bal.days_remaining} day${bal.days_remaining === 1 ? "" : "s"} left`}
            </span>
          </div>
        </div>
      </div>

      <div className="credits__hero admin-card">
        <div className="credits__figure">
          <div className={`credits__remaining credits__remaining--${storageTone}`}>
            {gb(bal.storage_gb)}
          </div>
          <div className="credits__figure-label">stored now</div>
        </div>

        <div className="credits__meter">
          <div className="credits__meter-head">
            <span>
              {gb(bal.peak_storage_gb)} peak of {gb(bal.included_storage_gb)}{" "}
              included
            </span>
            <span className="muted">{bal.storage_pct_used.toFixed(1)}%</span>
          </div>
          <div className="progress credits__bar">
            <div
              className={`progress__bar credits__bar-fill--${storageTone}`}
              style={{ width: `${storageBarPct}%` }}
            />
          </div>
          <div className="credits__meta muted">
            <span>documents and video across every case</span>
            <span>
              recorded on the highest daily reading, not the closing one
            </span>
            {storageOver && (
              <span>{gb(bal.storage_overage_gb)} over so far</span>
            )}
          </div>
        </div>
      </div>

      <div className="admin-card">
        <div className="admin-card__head">
          <div>
            <h2 className="admin-card__title">Where your credits went</h2>
            <p className="admin-card__blurb">
              This billing period only, grouped by the kind of work performed.
            </p>
          </div>
        </div>

        <div className="admin__scroll">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Activity</th>
                <th>Volume</th>
                <th>Times</th>
                <th>Credits</th>
                <th>Share</th>
              </tr>
            </thead>
            <tbody>
              {bal.breakdown.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted">
                    No credits used yet this period.
                  </td>
                </tr>
              )}
              {bal.breakdown.map((l) => {
                const share =
                  totalActionCredits > 0
                    ? (l.credits / totalActionCredits) * 100
                    : 0;
                return (
                  <tr key={l.action_key}>
                    <td>
                      <div className="admin-table__name">{l.label}</div>
                    </td>
                    <td>
                      {fmtQty(l.quantity)} {l.unit}
                      {l.quantity === 1 ? "" : "s"}
                    </td>
                    <td>{l.events.toLocaleString()}</td>
                    <td>{l.credits.toLocaleString()}</td>
                    <td>
                      <div className="progress-cell">
                        <div className="progress">
                          <div
                            className="progress__bar"
                            style={{ width: `${share}%` }}
                          />
                        </div>
                        <span className="progress-cell__pct">
                          {share.toFixed(0)}%
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {canSeeInvoices && (
        <div className="admin-card">
          <div className="admin-card__head">
            <div>
              <h2 className="admin-card__title">Usage statements</h2>
              <p className="admin-card__blurb">
                One statement per closed billing period. Consumption is frozen
                when the period closes, so a later change to your plan never
                alters what a period recorded. Your invoice is issued
                separately.
              </p>
            </div>
          </div>

          {invoiceErr && <div className="alert alert--error">{invoiceErr}</div>}

          <div className="admin__scroll">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Statement</th>
                  <th>Period</th>
                  <th>Plan</th>
                  <th>Credits</th>
                  <th>Overage</th>
                  <th>Storage</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 && (
                  <tr>
                    <td colSpan={8} className="muted">
                      No statements yet. Your first one appears once this
                      billing period closes.
                    </td>
                  </tr>
                )}
                {invoices.map((inv) => (
                  <tr key={inv.id}>
                    <td>
                      <button
                        type="button"
                        className="btn btn--link admin-table__name"
                        onClick={() => void openInvoice(inv)}
                      >
                        {inv.invoice_number}
                      </button>
                      <div className="admin-table__mono muted">
                        {fmtDate(inv.issued_at ? inv.issued_at.slice(0, 10) : null)}
                      </div>
                    </td>
                    <td>
                      {fmtDate(inv.period_start)}
                      <div className="muted admin-table__mono">
                        to {fmtDate(inv.period_end)}
                      </div>
                    </td>
                    <td>{inv.tier_name ?? <span className="muted">-</span>}</td>
                    <td>
                      {inv.credits_used.toLocaleString()}
                      <div className="muted admin-table__mono">
                        of {inv.included_credits.toLocaleString()}
                      </div>
                    </td>
                    <td>
                      {inv.overage_credits > 0 ? (
                        <>
                          {inv.overage_credits.toLocaleString()}
                          <div className="muted admin-table__mono">
                            credits over
                          </div>
                        </>
                      ) : (
                        <span className="muted">-</span>
                      )}
                    </td>
                    <td>
                      {hasStorageTerms(inv) ? (
                        <>
                          {gb(inv.peak_storage_gb)}
                          <div className="muted admin-table__mono">
                            {inv.storage_overage_gb > 0
                              ? `${gb(inv.storage_overage_gb)} over ${gb(
                                  inv.included_storage_gb,
                                )}`
                              : `of ${gb(inv.included_storage_gb)}`}
                          </div>
                        </>
                      ) : (
                        <span className="muted">not metered</span>
                      )}
                    </td>
                    <td>
                      <span
                        className={`invoice-status invoice-status--${inv.status}`}
                      >
                        {inv.status}
                      </span>
                    </td>
                    <td className="admin-table__actions">
                      <button
                        type="button"
                        className="btn"
                        onClick={() => void openInvoice(inv)}
                        disabled={detailBusy}
                      >
                        Open
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {detail && (
        <div
          className="modal"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) setDetail(null);
          }}
        >
          <div
            className="modal__card modal__card--lg modal__card--split"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="modal__header">
              <div className="modal__header-main">
                <h2 className="modal__header-title">{detail.invoice_number}</h2>
                <div className="muted">
                  {fmtDate(detail.period_start)} to {fmtDate(detail.period_end)}{" "}
                  &middot;{" "}
                  <span
                    className={`invoice-status invoice-status--${detail.status}`}
                  >
                    {detail.status}
                  </span>
                </div>
              </div>
              <button
                type="button"
                className="modal__close"
                onClick={() => setDetail(null)}
                aria-label="Close"
              >
                &times;
              </button>
            </header>

            <div className="modal__body">
              <div className="billing-stats">
                <div className="billing-stat">
                  <span className="billing-stat__label">
                    {detail.tier_name ?? "Plan"}
                  </span>
                  <span className="billing-stat__value">
                    {detail.credits_used.toLocaleString()}
                  </span>
                  <span className="billing-stat__hint">
                    credits used of {detail.included_credits.toLocaleString()}{" "}
                    included
                  </span>
                </div>
                <div className="billing-stat">
                  <span className="billing-stat__label">Overage</span>
                  <span className="billing-stat__value">
                    {detail.overage_credits.toLocaleString()}
                  </span>
                  <span className="billing-stat__hint">
                    credits past your allowance
                  </span>
                </div>
                {hasStorageTerms(detail) && (
                  <div className="billing-stat">
                    <span className="billing-stat__label">Storage</span>
                    <span className="billing-stat__value">
                      {gb(detail.peak_storage_gb)}
                    </span>
                    <span className="billing-stat__hint">
                      peak of {gb(detail.included_storage_gb)} included
                      {detail.storage_overage_gb > 0
                        ? `, ${gb(detail.storage_overage_gb)} over`
                        : ""}
                      {detail.peak_storage_on
                        ? ` (${fmtDate(detail.peak_storage_on)})`
                        : ""}
                    </span>
                  </div>
                )}
              </div>

              <h3 className="modal__section-title">Credit breakdown</h3>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Activity</th>
                    <th>Volume</th>
                    <th>Times</th>
                    <th>Credits</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.lines.length === 0 && (
                    <tr>
                      <td colSpan={4} className="muted">
                        No credit activity recorded for this period.
                      </td>
                    </tr>
                  )}
                  {detail.lines.map((l) => (
                    <tr key={l.action_key}>
                      <td>
                        <div className="admin-table__name">{l.label}</div>
                      </td>
                      <td>
                        {fmtQty(l.quantity)} {l.unit}
                        {l.quantity === 1 ? "" : "s"}
                      </td>
                      <td>{l.events.toLocaleString()}</td>
                      <td>{l.credits.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <footer className="modal__footer">
              <span className="muted modal__footer-hint">
                Questions about this statement? Contact your account manager.
              </span>
              <div className="modal__footer-actions">
                <button
                  type="button"
                  className="btn"
                  onClick={() => setDetail(null)}
                >
                  Close
                </button>
              </div>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
